import { Trans } from "@lingui/macro";
import { Button } from "@winamp/ui/components/Button/Button.js";
import { Message } from "@winamp/ui/components/Message/Message.js";
import { MessageContent } from "@winamp/ui/components/Message/MessageContent.js";
import { MessageDescription } from "@winamp/ui/components/Message/MessageDescription.js";
import { MessageTitle } from "@winamp/ui/components/Message/MessageTitle.js";
import { type ComponentProps, useEffect } from "react";
import { useFetcher, useRevalidator } from "react-router";
import { $path } from "safe-routes";
import { useOptionalAuth } from "~/modules/auth/hooks";
import { getDialogRoute } from "~/modules/route-dialogs/RouteDialogsContext";

interface KYCFeedbackProps extends ComponentProps<typeof Message> {
  kyc?: {
    status: "Declined" | "Pending" | "Unverified" | "Verified";
    statusColor: string;
  };
  showActiveStatus?: boolean;
  showPendingStatus?: boolean;
}

export function KYCFeedback({
  children,
  kyc,
  showActiveStatus = false,
  showPendingStatus = false,
  ...props
}: KYCFeedbackProps) {
  const auth = useOptionalAuth();
  const revalidator = useRevalidator();
  const refreshFetcher = useFetcher({ key: "session-refresh" });

  const isRefreshingUser = refreshFetcher.state !== "idle";

  function refreshUserSession() {
    refreshFetcher.submit(null, {
      action: $path("/auth/session/refresh"),
      method: "post",
    });
  }

  useEffect(() => {
    // refreshed user data for the first time
    if (refreshFetcher.data) {
      // Revalidate the current page
      revalidator.revalidate();
    }
  }, [refreshFetcher.data]);

  useEffect(() => {
    if (kyc && kyc.status !== auth?.user.kyc.status) {
      refreshUserSession();
    }
  }, [kyc?.status, auth?.user.kyc.status]);

  if (auth?.user.kyc.status === "Unverified") {
    return (
      <Message
        type="warning"
        className="max-lg:flex-col lg:items-center"
        {...props}
      >
        <MessageContent>
          <MessageTitle>
            <Trans>Identity verification</Trans>
          </MessageTitle>
          <MessageDescription>
            {children ?? (
              <Trans>
                Since your identity has not been verified, we have set your
                tracks as drafts. Please verify your identity and republish them
                to distribute on streaming platforms.
              </Trans>
            )}
          </MessageDescription>
        </MessageContent>
        <div className="lg:ml-auto">
          <Button
            color="orange"
            to={getDialogRoute("kyc.start")}
            preventScrollReset
          >
            <Trans>Verify now</Trans>
          </Button>
        </div>
      </Message>
    );
  }

  if (showActiveStatus && auth?.user.kyc.status === "Verified") {
    return (
      <Message
        type="success"
        className="max-lg:flex-col lg:items-center"
        {...props}
      >
        <MessageContent>
          <MessageTitle>
            <Trans>Your identity has been verified</Trans>
          </MessageTitle>
          <MessageDescription>
            <Trans>You have access to all services.</Trans>
          </MessageDescription>
        </MessageContent>
      </Message>
    );
  }

  if (showPendingStatus && auth?.user.kyc.status === "Pending") {
    return (
      <Message
        type="warning"
        className="max-lg:flex-col lg:items-center"
        {...props}
      >
        <MessageContent>
          <MessageTitle>
            <Trans>Your identity verification is pending</Trans>
          </MessageTitle>
          <MessageDescription>
            <Trans>
              During verification, you do not have access to all services.
            </Trans>
          </MessageDescription>
        </MessageContent>
        <div className="lg:ml-auto">
          <Button
            color="orange"
            loading={isRefreshingUser}
            onClick={() => refreshUserSession()}
          >
            <Trans>Refresh status</Trans>
          </Button>
        </div>
      </Message>
    );
  }

  if (auth?.user.kyc.status === "Declined") {
    return (
      <Message
        type="error"
        className="max-lg:flex-col lg:items-center"
        {...props}
      >
        <MessageContent>
          <MessageTitle>
            <Trans>Verify your identity</Trans>
          </MessageTitle>
          <MessageDescription>
            <Trans>
              Your identity verification has been declined. Please contact the
              support to unlock this situation.
            </Trans>
          </MessageDescription>
        </MessageContent>
        <div className="lg:ml-auto">
          <Button
            color="red"
            to={`mailto:support@winamp.com?subject=KYC%20Verification%20declined&body=Hello%20Winamp%20Support%2C%0D%0A%0D%0AMy%20KYC%20verification%20has%20been%20declined%20and%20I%20would%20like%20to%20see%20with%20you%20how%20I%20can%20fix%20this.%0D%0A%0D%0AHere%20are%20my%20details%20for%20your%20reference%3A%0D%0A%0D%0A%E2%80%A2%20My%20User%20UID%3A%20${auth?.user.uid}%0D%0A%E2%80%A2%20My%20User%20Email%3A%20${auth?.user.email}%0D%0A%E2%80%A2%20My%20First%20Name%3A%20${auth?.user.firstName}%0D%0A%E2%80%A2%20My%20Last%20Name%3A%20${auth?.user.lastName}%0D%0A%0D%0AThank%20you.`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Trans>Contact Support</Trans>
          </Button>
        </div>
      </Message>
    );
  }

  return null;
}
